import { useState, useEffect } from 'react';
import { message } from 'antd';
import {
  adminService,
  CreditPackage,
  CreateCreditPackageRequest,
  UpdateCreditPackageRequest,
} from '../../api/services/adminService';

export const useCreditPackages = () => {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState<CreditPackage | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const result = await adminService.getCreditPackages();
      setPackages(Array.isArray(result) ? result : []);
    } catch {
      message.error('Failed to load credit packages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values: CreateCreditPackageRequest) => {
    setSubmitting(true);
    try {
      await adminService.createCreditPackage(values);
      message.success('Credit package created');
      setModalVisible(false);
      fetchPackages();
    } catch {
      message.error('Failed to create credit package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id: string, values: UpdateCreditPackageRequest) => {
    setSubmitting(true);
    try {
      await adminService.updateCreditPackage(id, values);
      message.success('Credit package updated');
      setModalVisible(false);
      fetchPackages();
    } catch {
      message.error('Failed to update credit package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminService.deleteCreditPackage(id);
      message.success('Credit package deleted');
      fetchPackages();
    } catch {
      message.error('Failed to delete credit package');
    }
  };

  const openCreateModal = () => {
    setEditingPackage(null);
    setModalVisible(true);
  };

  const openEditModal = (pkg: CreditPackage) => {
    setEditingPackage(pkg);
    setModalVisible(true);
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  return {
    packages,
    loading,
    modalVisible,
    editingPackage,
    submitting,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch: fetchPackages,
  };
};
